import Remove from "@mui/icons-material/Remove";
import { Tooltip } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid-premium";
import { DataGrid } from "components/DataGrid";
import { HoverAction } from "components/HoverAction";
import { useOmnisearchDatagrid } from "hooks/useOmnisearchDatagrid";
import { useMemo } from "react";

import { getFacilityName } from "../../utils";
import { useCommunicationFacilities } from "../hooks/useCommunicationFacilities";
import { CommunicationFacility } from "../types";

export const FacilitiesDataGrid = ({
  facilityIds,
  removeFacility,
}: {
  facilityIds: string[];
  removeFacility: ((facility: CommunicationFacility) => void) | null;
}) => {
  const { paginationModel, sortModel, setPaginationModel, setSortModel } = useOmnisearchDatagrid({
    persistenceKey: "communication-facilities",
    initialSortModel: [{ field: "name", sort: "asc" }],
    initialPageSize: 10,
    isURLDriven: false,
  });

  const { data, previousData, loading } = useCommunicationFacilities({
    facilityIds,
    page: paginationModel.page,
    pageSize: paginationModel.pageSize,
    sort: [...sortModel],
  });

  const columns: GridColDef<CommunicationFacility>[] = useMemo(() => {
    return [
      {
        field: "name",
        headerName: "Facility",
        flex: 1.5,
        minWidth: 180,
        valueGetter: (_, row) => getFacilityName(row),
      },
      {
        field: "state",
        headerName: "State",
        flex: 0.3,
        minWidth: 80,
      },
      {
        field: "streetAddress1",
        headerName: "Street Address",
        flex: 1.2,
        minWidth: 200,
        valueGetter(_, row) {
          if (!row.streetAddress1) return "";
          const { streetAddress1, city, state, zip } = row;
          return [streetAddress1, city, state, zip].filter((s) => s).join(", ");
        },
      },
      {
        field: "lepc",
        headerName: "LEPC",
        flex: 0.8,
        minWidth: 140,
        valueGetter(_, row) {
          return row?.lepc?.name || "";
        },
      },
      {
        field: "fireDepartment",
        headerName: "Fire Department",
        flex: 0.8,
        minWidth: 140,
        valueGetter(_, row) {
          return row?.fireDepartment?.name || "";
        },
      },
      {
        field: "actions",
        type: "actions",
        sortable: false,
        filterable: false,
        minWidth: 80,
        getActions: ({ row }) => {
          const actions = [];

          if (removeFacility) {
            actions.push(
              <Tooltip title="Remove" key="remove">
                <HoverAction
                  label="Remove"
                  icon={<Remove fontSize="small" color="primary" />}
                  onClick={() => removeFacility(row)}
                />
              </Tooltip>
            );
          }
          return actions;
        },
      },
    ];
  }, [removeFacility]);

  const items = useMemo(() => {
    return data?.facilities.items ?? previousData?.facilities.items ?? [];
  }, [data?.facilities.items, previousData?.facilities.items]);
  const count = useMemo(() => {
    return data?.facilities.count ?? previousData?.facilities.count ?? 0;
  }, [data?.facilities.count, previousData?.facilities.count]);

  return (
    <DataGrid
      columns={columns}
      rows={items}
      rowCount={count}
      loading={loading}
      isRowSelectable={() => false}
      pagination
      paginationMode="server"
      paginationModel={paginationModel}
      onPaginationModelChange={setPaginationModel}
      sortingMode="server"
      sortModel={sortModel}
      onSortModelChange={setSortModel}
    />
  );
};
